import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DownOutlined, InboxOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import Autocomplete from '@mui/material/Autocomplete';

import MainCard from 'components/MainCard';
import AppCascaderSelect from './AppCascaderSelect';
import DramaChannelSelect from './DramaChannelSelect';

export const CHANNEL_OPTIONS = [
  { label: '全部渠道', value: '' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'TikTok', value: 'TikTok' },
  { label: 'KS', value: 'Ks' },
  { label: 'Smart', value: 'Smart' }
];

export const STATUS_OPTIONS = [
  { label: '全部状态', value: '' },
  { label: '启用', value: 1 },
  { label: '停用', value: 0 }
];

// 语种（对应旧项目 PUBLICITY_LANGUAGES / REGION_LIST_ZH）
export const LANGUAGE_OPTIONS = [
  { label: '简中', value: 'zh-Hans' },
  { label: '繁中', value: 'zh-Hant' },
  { label: '英语', value: 'en' },
  { label: '日语', value: 'ja' },
  { label: '韩语', value: 'ko' },
  { label: '泰语', value: 'th' },
  { label: '印尼', value: 'id' },
  { label: '马来语', value: 'ms' },
  { label: '越南', value: 'vi' },
  { label: '西语', value: 'es' },
  { label: '葡语', value: 'pt' },
  { label: '土耳其语', value: 'tr' },
  { label: '阿拉伯语', value: 'ar' },
  { label: '法语', value: 'fr' },
  { label: '德语', value: 'de' }
];

export const LANGUAGE_NAME_MAP = Object.fromEntries(LANGUAGE_OPTIONS.map((item) => [item.value, item.label]));

export function toLanguageName(value) {
  return LANGUAGE_NAME_MAP[value] || value || '--';
}

// 素材渠道（旧项目 materialManage channelOptions）
export const MATERIAL_CHANNEL_OPTIONS = [
  { label: 'Facebook', value: 'Facebook' },
  { label: 'TikTok', value: 'TikTok' },
  { label: 'KS', value: 'KS' }
];

// 文案投放渠道（旧项目 PROMOTION_CHANNEL，已去除 TikTok/KS，仅保留 FB/Smart）
export const PROMOTION_CHANNEL_OPTIONS = [
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Smart', value: 'Smart' },
  { label: 'KS', value: 'KS' }
];

// 素材类型（旧项目 materialTypeOptions）
export const MATERIAL_TYPE_OPTIONS = [
  { label: '原片', value: 'NATIVE' },
  { label: '混剪', value: 'MIXED_CUT' },
  { label: '解说', value: 'COMMENTARY' },
  { label: '口播推荐', value: 'ORAL_RECOMMENDATION' },
  { label: '评论/聊天式', value: 'COMMENT_CHAT' },
  { label: '预告片式', value: 'TRAILER' },
  { label: '二创包装', value: 'SECONDARY_CREATION_PACKAGE' },
  { label: 'AIGC素材', value: 'AIGC' },
  { label: '图片素材', value: 'IMAGE_MATERIAL' },
  { label: '其他', value: 'OTHER' }
];

// 钩子类型（旧项目 hookTypeOptions）
export const HOOK_TYPE_OPTIONS = [
  { label: '前贴', value: 'FRONT_STICKER' },
  { label: '冲突开场', value: 'CONFLICT_OPENING' },
  { label: '悬念提问', value: 'SUSPENSE_QUESTION' },
  { label: '反转开场', value: 'REVERSAL_OPENING' },
  { label: '爽点直给', value: 'DIRECT_REFRESHING_POINT' },
  { label: '高能台词', value: 'HIGH_ENERGY_LINES' },
  { label: '付费点前置', value: 'PAY_POINT_FRONT' },
  { label: '身份反差', value: 'IDENTITY_CONTRAST' },
  { label: '情绪崩溃', value: 'EMOTIONAL_BREAKDOWN' },
  { label: '亲密关系刺激', value: 'INTIMATE_RELATIONSHIP_STIMULATION' },
  { label: '金钱/阶层刺激', value: 'MONEY_CLASS_STIMULATION' },
  { label: '危机/威胁', value: 'CRISIS_THREAT' },
  { label: '视觉冲击', value: 'VISUAL_IMPACT' },
  { label: '其他', value: 'OTHER' },
  { label: '待标注', value: 'TO_BE_MARKED' }
];

export function materialTypeLabel(value) {
  return MATERIAL_TYPE_OPTIONS.find((item) => item.value === value)?.label || value;
}

export function hookTypeLabel(value) {
  return HOOK_TYPE_OPTIONS.find((item) => item.value === value)?.label || value;
}

// 日期格式化（旧项目 formatDate：MM-DD HH:mm）
export function formatDate(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(5, 16);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${month}-${day} ${hour}:${minute}`;
}

export function pickList(data) {
  if (Array.isArray(data)) return data;
  // 对齐旧项目 pickList：data.data 本身就是数组时直接返回（如分页对象 { data: [...] }）
  if (Array.isArray(data?.data)) return data.data;
  const list = data?.data?.list || data?.data?.items || data?.data?.programs || data?.list || data?.items || data?.programs || [];
  if (Array.isArray(list)) return list;
  return list?.list || list?.items || [];
}

// 应用筛选项（对齐旧项目 AppCascaderSelect：按 app_group_name 分级分组展示）
export function toAppFilterOptions(apps) {
  return (apps || []).map((item) => ({
    label: item.name || item.app_name || item.label || String(item.id ?? item.app_id ?? ''),
    value: item.id ?? item.app_id,
    app_group_name: item.app_group_name || item.group_name || '其他'
  }));
}

export function pickTotal(data, fallback = 0) {
  return Number(
    data?.data?.total ||
      data?.data?.total_count ||
      data?.data?.list?.total ||
      data?.data?.list?.total_count ||
      data?.total ||
      data?.total_count ||
      fallback ||
      0
  );
}

export function text(value) {
  if (value === undefined || value === null || value === '') return '--';
  if (Array.isArray(value)) return value.filter(Boolean).join('、') || '--';
  if (typeof value === 'object')
    return value.name || value.username || value.nickname || value.real_name || value.label || value.id || '--';
  return String(value);
}

export function toNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw || raw === '-' || raw === '--') return null;
  const number = Number(raw.replace(/[%\s]/g, '').replace(/,/g, ''));
  return Number.isFinite(number) ? number : null;
}

export function money(value) {
  const number = toNumber(value);
  if (number === null) return value === 0 || value === '0' ? '0.00' : '--';
  return number.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function percent(value) {
  if (value === undefined || value === null || value === '') return '--';
  if (typeof value === 'string' && value.includes('%')) {
    const number = toNumber(value);
    return number === null ? '--' : `${number.toFixed(2)}%`;
  }
  const number = toNumber(value);
  if (number === null) return '--';
  return `${(number > 1 ? number : number * 100).toFixed(2)}%`;
}

export function statusChip(value) {
  const normalized = String(value ?? '').toLowerCase();
  const enabled = value === 1 || normalized === '1' || normalized === 'enabled' || normalized === 'active';
  const disabled = value === 0 || normalized === '0' || normalized === 'disabled' || normalized === 'inactive';
  return (
    <Chip
      size="small"
      color={enabled ? 'success' : disabled ? 'default' : 'warning'}
      label={enabled ? '启用' : disabled ? '停用' : text(value)}
      variant={enabled ? 'filled' : 'outlined'}
    />
  );
}

export function formatRequestError(error) {
  // 被新请求顶替而取消的请求不是失败，返回空串避免页面闪错误提示
  if (error?.isCancel) return '';
  const message = error?.message || '';
  if (/failed to fetch|networkerror|load failed/i.test(message)) return '接口请求失败，请稍后重试';
  return message || '请求失败，请稍后重试';
}

export function BusyButton({ loading, loadingText, children, disabled, startIcon, ...props }) {
  return (
    <Button
      type="button"
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : startIcon}
      {...props}
    >
      {loading ? loadingText || '处理中...' : children}
    </Button>
  );
}

BusyButton.propTypes = {
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node
};

// 原生日期/时间输入：点击整个输入框即弹出选择器（不只点 icon）
// 默认紧凑（自适应宽度），需要撑满容器时传 fullWidth
function isDatePickerIconClick(event, input) {
  if (!event) return false;
  const rect = input.getBoundingClientRect();
  if (!rect || !rect.width) return false;
  // 原生日期/时间选择器图标固定在输入框最右侧约 24px 宽的区域
  return event.clientX >= rect.right - 24;
}

function openDatePickerIn(container, sourceEvent) {
  const input = container.querySelector('input');
  if (!input) return;
  // openViaClick 派发的合成点击（untrusted）直接交给浏览器默认行为，避免递归
  if (sourceEvent && sourceEvent.isTrusted === false) return;
  // 点击日历图标时图标本身会弹出原生选择器，交给浏览器，避免重复触发
  if (sourceEvent && isDatePickerIconClick(sourceEvent, input)) return;
  input.focus();
  const openViaClick = () => input.click();
  if (typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      return;
    } catch {
      // showPicker 不可用/被拦截（非用户激活等场景）时退化为点击原生控件
    }
  }
  openViaClick();
}

export function DateField({ label, value, onChange, type = 'date', min, max, minWidth, fullWidth = false, slotProps, ...rest }) {
  const boxRef = useRef(null);

  const openPicker = (event) => {
    openDatePickerIn(event.currentTarget, event);
  };

  const inputValue = value ? (type === 'datetime-local' ? String(value).slice(0, 16) : String(value).slice(0, 10)) : '';

  return (
    <Box
      ref={boxRef}
      onClick={openPicker}
      sx={{
        cursor: 'pointer',
        ...(fullWidth ? { width: '100%' } : {}),
        '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' }
      }}
    >
      <TextField
        size="small"
        type={type}
        label={label}
        value={inputValue}
        onChange={(event) => {
          if (event.target.validity.valid) onChange(event.target.value);
        }}
        slotProps={{
          ...slotProps,
          inputLabel: { shrink: true, ...slotProps?.inputLabel },
          htmlInput: { ...slotProps?.htmlInput, min, max }
        }}
        fullWidth={fullWidth}
        {...rest}
        sx={{
          ...(minWidth ? { minWidth: { sm: minWidth } } : {}),
          '& .MuiInputBase-input': { height: 40, boxSizing: 'border-box', cursor: 'pointer' },
          '& input[type="date"]::-webkit-calendar-picker-indicator, & input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
            cursor: 'pointer'
          }
        }}
      />
    </Box>
  );
}

DateField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  type: PropTypes.oneOf(['date', 'datetime-local']),
  min: PropTypes.string,
  max: PropTypes.string,
  minWidth: PropTypes.number,
  fullWidth: PropTypes.bool,
  slotProps: PropTypes.object
};

function getDateRangeLimits(field, values = {}) {
  const name = field.name || '';
  let startName = '';
  let endName = '';

  if (name.endsWith('_start')) endName = name.replace(/_start$/, '_end');
  else if (name.endsWith('_end')) startName = name.replace(/_end$/, '_start');
  else if (name.startsWith('start_')) endName = name.replace(/^start_/, 'end_');
  else if (name.startsWith('end_')) startName = name.replace(/^end_/, 'start_');

  return {
    min: startName ? values[startName] || undefined : undefined,
    max: endName ? values[endName] || undefined : undefined
  };
}

export function FieldControl({ field, value, values, onChange }) {
  const commonSx = {
    width: '100%',
    '& .MuiInputBase-root': { height: 40, boxSizing: 'border-box' },
    '& .MuiInputLabel-root': { lineHeight: 2 }
  };

  // 远程搜索防抖：避免每次输入都触发接口请求（对齐旧项目 $remoteDebounce）
  const searchTimerRef = useRef(null);
  const [remoteSelectOpen, setRemoteSelectOpen] = useState(false);
  const handleRemoteSearch = (keyword) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => field.onSearch?.(keyword, values?.[field.sourceName]), 300);
  };

  if (field.type === 'dramaSearch') {
    return (
      <Box sx={{ display: 'flex', width: field.width || 340 }}>
        <DramaChannelSelect value={values?.[field.sourceName] || ''} onChange={(source) => onChange(field.sourceName, source)} />
        <TextField
          size="small"
          placeholder={field.placeholder || '请输入剧名或剧ID'}
          value={value ?? ''}
          onChange={(event) => onChange(field.name, event.target.value)}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
        />
      </Box>
    );
  }

  if (field.type === 'appCascader') {
    const cascaderValue = Array.isArray(value) ? value : value === '' || value === undefined || value === null ? [] : [value];
    return (
      <Box sx={{ ...commonSx, width: field.width || 220, minWidth: field.width || 220 }}>
        <AppCascaderSelect
          value={cascaderValue}
          onChange={(next) => onChange(field.name, next)}
          options={field.options || []}
          placeholder={field.placeholder || `请选择${field.label || '应用'}`}
          width={field.width || 220}
          multiple={field.multiple}
          clearable={field.clearable}
        />
      </Box>
    );
  }

  if (field.type === 'select') {
    const options = field.options || [];
    const multiple = !!field.multiple;
    // 多选时用数组值（对齐旧项目 el-select multiple），单选保持原字符串
    const selectValue = multiple ? (Array.isArray(value) ? value : value == null || value === '' ? [] : [value]) : (value ?? '');
    // 分级分组选择（对齐旧项目 AppCascaderSelect：按 app_group_name 分组展示）
    const groups = field.grouped
      ? (() => {
          const groupMap = new Map();
          options.forEach((option) => {
            const groupName = option.app_group_name || '其他';
            if (!groupMap.has(groupName)) groupMap.set(groupName, []);
            groupMap.get(groupName).push(option);
          });
          return [...groupMap.entries()];
        })()
      : [];
    return (
      <FormControl size="small" sx={{ ...commonSx, minWidth: { sm: field.width || 160 } }}>
        <InputLabel shrink>{field.label}</InputLabel>
        <Select
          label={field.label}
          multiple={multiple}
          value={selectValue}
          displayEmpty
          onChange={(event) => onChange(field.name, event.target.value)}
          sx={{ height: 40, '& .MuiSelect-select': { display: 'flex', alignItems: 'center' } }}
        >
          {field.grouped
            ? groups.flatMap(([groupName, children]) => [
                <ListSubheader
                  key={`${field.name}-${groupName}`}
                  sx={{ lineHeight: '28px', fontSize: 12, color: 'text.secondary', bgcolor: 'transparent' }}
                >
                  {groupName}
                </ListSubheader>,
                ...children.map((option) => (
                  <MenuItem key={`${field.name}-${option.value}`} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ])
            : options.map((option) => (
                <MenuItem key={`${field.name}-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
        </Select>
      </FormControl>
    );
  }

  if (field.type === 'remoteSelect') {
    const selectedSource = field.sourceName ? values?.[field.sourceName] : '';
    const options = (field.options || []).filter((option) => !selectedSource || !option.source || option.source === selectedSource);
    const current = options.find((option) => String(option.id ?? option.value ?? '') === String(value ?? '')) || null;
    const search = (
      <Autocomplete
        size="small"
        sx={{ ...commonSx, minWidth: { sm: field.width || 200 } }}
        options={options}
        getOptionLabel={(option) => option.label || String(option.id ?? option.value ?? '')}
        getOptionKey={(option) => option.id ?? option.value ?? option.label}
        value={current}
        open={remoteSelectOpen}
        filterOptions={(items) => items}
        onOpen={() => setRemoteSelectOpen(true)}
        onClose={() => setRemoteSelectOpen(false)}
        onInputChange={(_, keyword, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setRemoteSelectOpen(true);
            handleRemoteSearch(keyword);
          }
        }}
        onChange={(_, option) => {
          setRemoteSelectOpen(false);
          onChange(field.name, option ? (option.id ?? option.value ?? '') : '');
        }}
        renderInput={(params) => (
          <TextField
            id={params.id}
            disabled={params.disabled}
            fullWidth={params.fullWidth}
            size={params.size}
            label={field.label}
            placeholder={field.placeholder || `请输入${field.label || '关键词'}`}
            slotProps={{
              inputLabel: params.slotProps?.inputLabel,
              input: params.slotProps?.input,
              htmlInput: params.slotProps?.htmlInput
            }}
          />
        )}
      />
    );
    if (!field.sourceName) return search;
    return (
      <Box sx={{ display: 'flex', minWidth: field.width || 300 }}>
        <DramaChannelSelect
          value={values?.[field.sourceName] || ''}
          onChange={(source) => {
            onChange(field.sourceName, source);
            onChange(field.name, '');
          }}
        />
        {search}
      </Box>
    );
  }

  if (field.type === 'switch') {
    return (
      <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={<Switch checked={!!value} onChange={(event) => onChange(field.name, event.target.checked)} />}
          label={field.label}
          sx={{ m: 0 }}
        />
      </Box>
    );
  }

  const isDate = field.type === 'date' || field.type === 'datetime-local';

  if (isDate) {
    const rangeLimits = getDateRangeLimits(field, values);
    return (
      <Box sx={{ width: '100%' }}>
        <DateField
          fullWidth
          label={field.label}
          type={field.type}
          value={value}
          onChange={(val) => onChange(field.name, val)}
          minWidth={field.width || 180}
          {...rangeLimits}
        />
      </Box>
    );
  }

  return (
    <TextField
      size="small"
      label={field.label}
      type={field.type || 'text'}
      value={value ?? ''}
      onChange={(event) => onChange(field.name, event.target.value)}
      multiline={field.multiline}
      minRows={field.multiline ? 3 : undefined}
      sx={{
        ...commonSx,
        minWidth: { sm: field.width || 180 },
        '& .MuiInputBase-input': {
          height: field.multiline ? 'auto' : 40,
          boxSizing: 'border-box'
        }
      }}
    />
  );
}

FieldControl.propTypes = {
  field: PropTypes.object,
  value: PropTypes.any,
  values: PropTypes.object,
  onChange: PropTypes.func
};

export function FilterBar({ fields, values, onChange, onSearch, onReset, extraActions, loading, sticky = true }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(values);

  const prevValuesRef = useRef(values);
  useEffect(() => {
    if (JSON.stringify(prevValuesRef.current) !== JSON.stringify(values)) {
      setDraft(values);
      prevValuesRef.current = values;
    }
  }, [values]);

  const handleDraftChange = (name, value) => {
    const nextDraft = { ...draft, [name]: value };
    setDraft(nextDraft);
    if (fields.some((field) => field.sourceName === name && field.applyOnSourceChange)) {
      onSearch?.(nextDraft);
    }
  };

  const handleSearch = () => {
    onSearch?.(draft);
  };

  const handleReset = () => {
    onReset();
  };

  const visibleFields = isMobile && !expanded ? fields.slice(0, 1) : fields;

  return (
    <MainCard
      contentSX={{ p: { xs: 1.5, sm: 2.25 } }}
      // 列表页滚动时查询项固定于顶部(AppBar 下方),避免数据长列表时筛选项滚出视野
      sx={sticky ? { position: 'sticky', top: 76, zIndex: 1100 } : undefined}
    >
      <Stack spacing={1.5}>
        <Grid container spacing={1.25} sx={{ alignItems: 'center' }}>
          {visibleFields.map((field) => (
            <Grid key={field.name} size={{ xs: 12, sm: 'auto' }} sx={{ minWidth: { sm: field.width || 160 } }}>
              <FieldControl field={field} value={draft[field.name]} values={draft} onChange={handleDraftChange} />
            </Grid>
          ))}
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, alignItems: 'center' }}>
              <BusyButton
                variant="contained"
                startIcon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                loadingText="查询中..."
                sx={{ height: 36, flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
              >
                查询
              </BusyButton>
              <Button
                variant="outlined"
                startIcon={<ReloadOutlined />}
                onClick={handleReset}
                sx={{ height: 36, flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
              >
                重置
              </Button>
              {isMobile && fields.length > 1 && (
                <Button
                  variant="text"
                  onClick={() => setExpanded((prev) => !prev)}
                  endIcon={expanded ? <UpOutlined /> : <DownOutlined />}
                  sx={{ height: 36, minWidth: 36, whiteSpace: 'nowrap' }}
                >
                  筛选
                </Button>
              )}
            </Stack>
          </Grid>
          {extraActions && (
            <Grid size={{ xs: 12, sm: 'auto' }} sx={{ marginLeft: { sm: 'auto' } }}>
              {extraActions}
            </Grid>
          )}
        </Grid>
        {isMobile && !expanded && fields.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            共 {fields.length} 个筛选项,点击「筛选」展开
          </Typography>
        )}
      </Stack>
    </MainCard>
  );
}

FilterBar.propTypes = {
  fields: PropTypes.array,
  values: PropTypes.object,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onReset: PropTypes.func,
  extraActions: PropTypes.node,
  loading: PropTypes.bool,
  sticky: PropTypes.bool
};

export function DataTable({
  columns,
  rows,
  loading,
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  actions,
  sx,
  hidePagination = false,
  getRowSx
}) {
  const stickyActionSx = actions
    ? {
        position: 'sticky',
        right: 0,
        zIndex: 2,
        bgcolor: 'background.paper',
        boxShadow: (theme) => `-8px 0 12px -12px ${theme.vars.palette.divider}`
      }
    : {};

  return (
    <MainCard content={false} sx={{ display: 'flex', flexDirection: 'column', minHeight: 460, flex: 1, ...sx }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ border: 0, borderRadius: 1, maxWidth: '100%', overflowX: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Table size="small" stickyHeader sx={{ flex: rows.length === 0 ? 1 : 'initial' }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || 'left'}
                  sx={{
                    minWidth: column.minWidth,
                    bgcolor: 'grey.50',
                    fontWeight: 700,
                    whiteSpace: column.nowrap === false ? 'normal' : 'nowrap',
                    ...(column.sticky ? { position: 'sticky', left: 0, zIndex: 4 } : {})
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {actions && (
                <TableCell
                  align="center"
                  sx={{
                    ...stickyActionSx,
                    bgcolor: 'grey.50',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    minWidth: 100,
                    pr: 2,
                    zIndex: 4
                  }}
                >
                  操作
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align || 'left'}>
                      <Skeleton variant="text" width="70%" />
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell align="right" sx={{ ...stickyActionSx, pr: 2 }}>
                      <Skeleton variant="text" width={40} sx={{ ml: 'auto' }} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!loading &&
              rows.map((row, index) => {
                const rowSx = getRowSx?.(row) || {};
                const stickyBg = rowSx.bgcolor || 'background.paper';
                return (
                  <TableRow key={row.id || row.rowKey || index} hover={!row.isTotal} sx={rowSx}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align || 'left'}
                        sx={{
                          whiteSpace: column.nowrap === false ? 'normal' : 'nowrap',
                          verticalAlign: column.nowrap === false ? 'top' : 'middle',
                          minWidth: column.minWidth,
                          ...(column.sticky ? { position: 'sticky', left: 0, zIndex: 1, bgcolor: stickyBg } : {})
                        }}
                      >
                        {column.render ? column.render(row) : text(row[column.key])}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell align="right" sx={{ ...stickyActionSx, whiteSpace: 'nowrap', pr: 2 }}>
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            {!loading && rows.length === 0 && (
              <TableRow sx={{ height: '100%' }}>
                <TableCell colSpan={columns.length + (actions ? 1 : 0)} align="center" sx={{ py: 10, height: '100%' }}>
                  <Stack spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'grey.100',
                        display: 'grid',
                        placeItems: 'center',
                        color: 'text.disabled'
                      }}
                    >
                      <InboxOutlined style={{ fontSize: 36 }} />
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      暂无数据
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      调整筛选条件后重试
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!hidePagination && (
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={perPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
          onRowsPerPageChange={(event) => onPerPageChange(Number(event.target.value))}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      )}
    </MainCard>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
  loading: PropTypes.bool,
  page: PropTypes.number,
  perPage: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  onPerPageChange: PropTypes.func,
  actions: PropTypes.func,
  hidePagination: PropTypes.bool,
  getRowSx: PropTypes.func,
  sx: PropTypes.object
};

export function EditDialog({ open, title, fields, values, onChange, onClose, onSubmit, submitting }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fields.map((field) => (
            <FieldControl key={field.name} field={field} value={values[field.name]} values={values} onChange={onChange} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button type="button" onClick={onClose} disabled={submitting} sx={{ minWidth: 80, height: 36 }}>
          取消
        </Button>
        <BusyButton variant="contained" onClick={onSubmit} loading={submitting} loadingText="保存中..." sx={{ minWidth: 80, height: 36 }}>
          保存
        </BusyButton>
      </DialogActions>
    </Dialog>
  );
}

EditDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  fields: PropTypes.array,
  values: PropTypes.object,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool
};

export function usePagedList(fetcher, initialFilters = {}, options = {}) {
  // 部分页面（如广告发布/模板管理）共用一个组件但只展示其中一个列表，
  // 未激活列表不应在后台提前请求，避免误发草稿查询并造成无意义的空列表请求。
  const enabled = options.enabled !== false;
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(options.perPage || 20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestVersionRef = useRef(0);

  const params = useMemo(() => ({ ...filters, page, per_page: perPage }), [filters, page, perPage]);

  const load = useCallback(async () => {
    const requestVersion = ++requestVersionRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await fetcher(params);
      if (requestVersion !== requestVersionRef.current) return;
      const list = pickList(response);
      setRows(list);
      setTotal(pickTotal(response, list.length));
    } catch (err) {
      if (requestVersion !== requestVersionRef.current) return;
      setRows([]);
      setTotal(0);
      setError(formatRequestError(err));
    } finally {
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  }, [fetcher, params]);

  const search = useCallback((nextFilters) => {
    setPage(1);
    setFilters(nextFilters);
  }, []);

  useEffect(() => {
    if (!enabled) {
      requestVersionRef.current += 1;
      setLoading(false);
      return undefined;
    }
    load();
    // 切换筛选或隐藏列表时，旧请求即使成功也不能重新填入已失效的视图。
    return () => {
      requestVersionRef.current += 1;
    };
  }, [enabled, load]);

  return {
    filters,
    setFilters,
    rows,
    setRows,
    total,
    setTotal,
    page,
    setPage,
    perPage,
    setPerPage,
    loading,
    error,
    setError,
    load,
    search,
    reset: () => {
      const todayStr = new Date().toISOString().slice(0, 10);
      const nextFilters = { ...initialFilters };
      Object.keys(nextFilters).forEach((key) => {
        if ((key.includes('date') || key === 'start' || key === 'end') && (!nextFilters[key] || nextFilters[key] === '')) {
          nextFilters[key] = todayStr;
        }
      });
      setFilters(nextFilters);
      setPage(1);
    }
  };
}

export function CrudPage({ title, filterFields, initialFilters, columns, formFields, emptyForm, fetcher, create, update }) {
  const list = usePagedList(fetcher, initialFilters);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const changeFilter = (name, value) => {
    list.setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openCreate = () => {
    setEditingRow(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setForm({ ...emptyForm, ...row });
    setDialogOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (editingRow?.id) {
        const oldRows = [...list.rows];
        list.setRows((prev) => prev.map((r) => (r.id === editingRow.id ? { ...r, ...form } : r)));
        try {
          await update(editingRow.id, form);
        } catch (err) {
          list.setRows(oldRows);
          throw err;
        }
      } else {
        await create(form);
        list.setPage(1);
        await list.load();
      }
      setDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <FilterBar
        fields={filterFields}
        values={list.filters}
        onChange={changeFilter}
        onSearch={list.search}
        onReset={list.reset}
        loading={list.loading}
        extraActions={
          create && (
            <BusyButton
              variant="contained"
              startIcon={<PlusOutlined />}
              onClick={openCreate}
              sx={{ height: 36, width: { xs: '100%', sm: 'auto' } }}
            >
              新增
            </BusyButton>
          )
        }
      />
      {list.error && <Chip color="error" label={list.error} sx={{ alignSelf: 'flex-start', borderRadius: 1 }} />}
      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        page={list.page}
        perPage={list.perPage}
        total={list.total}
        onPageChange={list.setPage}
        onPerPageChange={(value) => {
          list.setPerPage(value);
          list.setPage(1);
        }}
        actions={
          update
            ? (row) => (
                <Button size="small" onClick={() => openEdit(row)} sx={{ whiteSpace: 'nowrap' }}>
                  编辑
                </Button>
              )
            : null
        }
      />
      <EditDialog
        open={dialogOpen}
        title={editingRow ? `编辑${title}` : `新增${title}`}
        fields={formFields}
        values={form}
        onChange={(name, value) => setForm((prev) => ({ ...prev, [name]: value }))}
        onClose={() => setDialogOpen(false)}
        onSubmit={submit}
        submitting={submitting}
      />
    </Stack>
  );
}

CrudPage.propTypes = {
  title: PropTypes.string,
  filterFields: PropTypes.array,
  initialFilters: PropTypes.object,
  columns: PropTypes.array,
  formFields: PropTypes.array,
  emptyForm: PropTypes.object,
  fetcher: PropTypes.func,
  create: PropTypes.func,
  update: PropTypes.func
};
